import {
  ConstraintType,
  IProblem,
  Price,
  CandidateTransportationItem,
  CandidatePoiItem,
} from '@mobi-components/global-solution';
import {RecommendationContext, ProblemDestination} from '../state/global.types';
import {v4} from 'uuid';

import {buildProblem} from './build-problem';
import {FilterTag} from '@/views/amex/app-types';
import {submitProblem} from '../solution/get-problem.action';
import {SolutionResponse} from '../solution/solution-support.types';
import {getDateInTimezone} from '../state/utils';
import {defaultDestination} from '../state/initial-state';
import {SeatingType} from '@common/travelers-filter/travelers.types';
import {getIntersectionalFilters} from '../state/helpers';

export interface ISolutionVersion extends SolutionResponse {
  theme: string;
}

export function generateActivityForPoi(
  id: string,
  activityName: string | null,
  candidateItemName: string,
  scheduled_start_time = 0,
  scheduled_end_time = 0,
) {
  return {
    start_constraint_type: ConstraintType.NOT_EARLIER_THAN,
    end_constraint_type: ConstraintType.NOT_LATER_THAN,
    candidate_information: {
      id,
      name: activityName,
      candidates: [
        {
          id,
          name: candidateItemName,
          start_timestamp: Math.floor(scheduled_start_time / 1000),
          end_timestamp: Math.floor(scheduled_end_time / 1000),
        },
      ],
    },
  };
}

function buildAMEXProblem(
  travelersInformation: TravelersIndormation,
  favorites: RecommendationContext[],
  additionalItems: RecommendationContext[],
  knownActivities: RecommendationContext[],
  origin: ProblemDestination,
  destination: ProblemDestination,
  userId: string,
  tags: FilterTag[],
  departureDate: number,
  arriivalDate: number,
  context: CandidatePoiItem,
  price: Price[] | null = null,
  additionalContexts: CandidatePoiItem[] = [],
) {
  const intersectionalFilters = getIntersectionalFilters();
  const activities = favorites.map((x: RecommendationContext) => {
    return generateActivityForPoi(x.id, null, x.name, x.scheduled_start_time, x.scheduled_end_time);
  });
  const additionalActivities = additionalItems.map((x: RecommendationContext) => {
    const isItKnown = knownActivities.findIndex((y) => y.id === x.originalId || y.name === x.name) >= 0;
    if (x.timezone) {
      const minTime = departureDate;
      const maxTime = arriivalDate;

      const startDate = getDateInTimezone(new Date(x.scheduled_start_time), x.timezone).valueOf();
      const endDate = getDateInTimezone(new Date(x.scheduled_end_time), x.timezone).valueOf();

      x.scheduled_start_time = minTime > startDate ? minTime : startDate;
      x.scheduled_end_time = maxTime < endDate ? maxTime : endDate;
    }
    const poi = generateActivityForPoi(
      x.id,
      isItKnown ? null : x.name,
      x.name,
      x.scheduled_start_time,
      x.scheduled_end_time,
    );
    poi.start_constraint_type = ConstraintType.NOT_EARLIER_THAN;
    poi.end_constraint_type = ConstraintType.NOT_LATER_THAN;
    return poi;
  });
  const contextPOI =
    (context && {
      candidate_information: {
        candidates: [context],
        id: context.id,
        name: context.name,
      },
    }) ||
    null;

  const additionalContextsPOIs = additionalContexts
    .sort((a, b) => a.end_timestamp - b.end_timestamp)
    .map((x) => ({
      candidate_information: {
        candidates: [x],
        id: x.id,
        name: x.name,
      },
    }));

  const dest: ProblemDestination = context
    ? {
        ...defaultDestination,
        name: context.name,
        lat: context.location?.lat,
        lng: context.location?.lng,
        time_zone: context.location?.time_zone,
        // @ts-ignore
        organically_explored: context.organically_explored,
        arrival_timestamp: additionalContextsPOIs.length ? undefined : Math.floor(arriivalDate / 1000),
      }
    : {...destination, arrival_timestamp: Math.floor(arriivalDate / 1000)};
  const problem = buildProblem(
    v4(),
    userId,
    {...origin, departure_timestamp: Math.floor(departureDate / 1000)},
    dest,
    tags,
    [],
    travelersInformation,
    intersectionalFilters,
  );
  if (!additionalContextsPOIs.length) {
    // @ts-ignore
    problem.activities = [...activities, ...additionalActivities, contextPOI];
  } else {
    // @ts-ignore
    problem.activities = [...activities, ...additionalActivities, ...additionalContextsPOIs];
  }
  problem.budget = price ? {price} : null;

  problem.mode = process.env.VUE_APP_MODE;
  return problem;
}

// TODO: move this into types file.
export interface TravelersIndormation {
  adults: number;
  children: number;
  child_ages: number[];
  infants: number;
  infant_types: SeatingType;
  rooms: number;
}

export async function submitAmexProblem(
  travelerInformation: TravelersIndormation,
  tripId: string,
  tripContext: CandidatePoiItem,
  favorites: RecommendationContext[],
  additionalItems: RecommendationContext[],
  knownActivities: RecommendationContext[],
  origin: ProblemDestination,
  destination: ProblemDestination,
  userId: string,
  tags: FilterTag[],
  departureDate: number,
  arriivalDate: number,
  price: Price[] | null = null,
  flights: CandidateTransportationItem[] = [],
  additionalContexts: CandidatePoiItem[] = [],
): Promise<SolutionResponse> {
  const problem = buildAMEXProblem(
    travelerInformation,
    favorites,
    additionalItems,
    knownActivities,
    origin,
    destination,
    userId,
    tags,
    departureDate,
    arriivalDate,
    tripContext,
    price,
    additionalContexts,
  );
  flights.forEach((x) => {
    // @ts-ignore
    problem.activities.unshift({
      // @ts-ignore
      candidate_information: {candidates: [x]},
    });
  });
  problem.id = tripId;
  // @ts-ignore
  return submitProblem((problem as unknown) as IProblem);
}
